import { ExternalLink, TrendingUp, AlertTriangle, Info } from "lucide-react";
import type { EpssScore as EpssScoreType } from "@/lib/api";

interface EpssScoreProps {
  epss: EpssScoreType;
  showCveLink?: boolean;
  compact?: boolean;
  className?: string;
}

function getRiskAssessment(epss: number): {
  label: string;
  color: string;
  bgColor: string;
  description: string;
} {
  if (epss >= 0.5) {
    return {
      label: "Very High",
      color: "text-red-400",
      bgColor: "bg-red-500/10",
      description: "Actively exploited in the wild",
    };
  }
  if (epss >= 0.2) {
    return {
      label: "High",
      color: "text-orange-400",
      bgColor: "bg-orange-500/10",
      description: "Likely to be exploited soon",
    };
  }
  if (epss >= 0.1) {
    return {
      label: "Elevated",
      color: "text-amber-400",
      bgColor: "bg-amber-500/10",
      description: "Increased exploitation risk",
    };
  }
  if (epss >= 0.01) {
    return {
      label: "Moderate",
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/10",
      description: "Some exploitation activity",
    };
  }
  return {
    label: "Low",
    color: "text-slate-400",
    bgColor: "bg-slate-500/10",
    description: "Minimal exploitation activity",
  };
}

function getPercentileColor(percentile: number): string {
  if (percentile >= 0.9) return "bg-red-500";
  if (percentile >= 0.75) return "bg-orange-500";
  if (percentile >= 0.5) return "bg-amber-500";
  if (percentile >= 0.25) return "bg-cyan-500";
  return "bg-slate-500";
}

export function EpssScore({
  epss,
  showCveLink = true,
  compact = false,
  className = "",
}: EpssScoreProps) {
  const assessment = getRiskAssessment(epss.epss);
  const epssPercent = (epss.epss * 100).toFixed(1);
  const percentilePercent = (epss.percentile * 100).toFixed(0);
  const percentileColor = getPercentileColor(epss.percentile);

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <span
          className={`
            px-1.5 py-0.5 rounded text-xs font-mono font-semibold
            ${assessment.bgColor} ${assessment.color}
          `}
        >
          {epssPercent}%
        </span>
        {showCveLink && (
          <a
            href={`https://nvd.nist.gov/vuln/detail/${epss.cve}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-cyan-400 transition-colors font-mono"
          >
            {epss.cve}
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      className={`
        rounded-lg border border-border/50 bg-card/50 p-4
        ${className}
      `}
    >
      {/* Header with CVE */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-amber-500/10">
            <TrendingUp className="h-4 w-4 text-amber-400" />
          </div>
          <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            EPSS Score
          </span>
        </div>
        {showCveLink && (
          <a
            href={`https://nvd.nist.gov/vuln/detail/${epss.cve}`}
            target="_blank"
            rel="noopener noreferrer"
            className="
              inline-flex items-center gap-1 px-2 py-1 rounded
              text-xs font-mono text-muted-foreground
              hover:text-cyan-400 hover:bg-cyan-500/10
              border border-border/50 hover:border-cyan-500/30
              transition-all duration-200
            "
          >
            {epss.cve}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      {/* Main score display */}
      <div className="flex items-end gap-4 mb-4">
        <div>
          <span
            className={`
              text-3xl font-mono font-bold tracking-tight
              ${assessment.color}
            `}
          >
            {epssPercent}%
          </span>
          <span className="text-xs text-muted-foreground ml-1">probability</span>
        </div>
        <div
          className={`
            px-2 py-1 rounded text-xs font-semibold uppercase tracking-wider
            ${assessment.bgColor} ${assessment.color}
          `}
        >
          {assessment.label}
        </div>
      </div>

      {/* Percentile bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-muted-foreground">Percentile Rank</span>
          <span className="font-mono font-semibold">Top {(100 - parseFloat(percentilePercent)).toFixed(0)}%</span>
        </div>
        <div className="relative h-2 bg-muted/30 rounded-full overflow-hidden">
          {/* Gradient background showing scale */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-600 via-amber-500 to-red-500 opacity-20" />
          {/* Actual percentile bar */}
          <div
            className={`
              absolute inset-y-0 left-0 rounded-full transition-all duration-700
              ${percentileColor}
            `}
            style={{ width: `${percentilePercent}%` }}
          />
          {/* Marker line */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg"
            style={{ left: `${percentilePercent}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground/60 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Risk description */}
      <div className="flex items-start gap-2 p-2 rounded bg-muted/20">
        {epss.epss >= 0.1 ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
        ) : (
          <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
        )}
        <span className="text-xs text-muted-foreground leading-relaxed">
          {assessment.description}. This vulnerability is in the top{" "}
          <span className="font-semibold text-foreground">
            {(100 - parseFloat(percentilePercent)).toFixed(0)}%
          </span>{" "}
          most likely to be exploited within the next 30 days.
        </span>
      </div>

      {/* Update date */}
      {epss.date && (
        <div className="mt-2 text-[10px] text-muted-foreground/50 text-right">
          Updated: {new Date(epss.date).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}

// Inline EPSS indicator for tables
export function EpssIndicator({ epss }: { epss: number }) {
  const percent = (epss * 100).toFixed(1);
  const assessment = getRiskAssessment(epss);

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-1.5 py-0.5 rounded
        text-xs font-mono font-medium
        ${assessment.bgColor} ${assessment.color}
      `}
      title={`EPSS: ${percent}% - ${assessment.description}`}
    >
      <TrendingUp className="h-3 w-3" />
      {percent}%
    </span>
  );
}

export default EpssScore;
